import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import { afterReceiving, beforeSending } from '@/services/activity/hooks'

const resource = 'activities'

export type ActivityType = 'task' | 'call' | 'meeting' | 'email_logged' | 'note' | 'stage_change' | 'system'
export type ActivityStatus = 'todo' | 'done' | 'canceled' | 'n/a'
export type ActivityDueFilter = 'today' | 'overdue' | 'upcoming' | 'none'
export type ActivityScope = 'mine' | 'team' | 'all'

export type ActivityListQuery = {
  page?: number
  limit?: number
  scope?: ActivityScope
  assigneeId?: string
  companyId?: string
  companyIds?: string
  contactId?: string
  engagementId?: string
  type?: string
  status?: string
  due?: ActivityDueFilter
  search?: string
}

export type ActivityPayload = {
  type: ActivityType
  subject?: string
  body?: string
  assigneeId?: string
  companyId?: string
  contactId?: string
  engagementId?: string
  dueAt?: Date | string | null
  scheduledAt?: Date | string | null
  priority?: number
  taggedIds?: string[]
  status?: ActivityStatus
  meta?: {
    direction?: 'inbound' | 'outbound'
    source?: string
  }
}

const list = (axios: IFactoryFunctionRequest<any, any>) => (query: ActivityListQuery = {}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    query,
    afterReceivingHook: afterReceiving,
  })
}

const getById = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
    afterReceivingHook: afterReceiving,
  })
}

const create = (axios: IFactoryFunctionRequest<any, any>) => (data: ActivityPayload) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

const update = (axios: IFactoryFunctionRequest<any, any>) => (id: string, data: Partial<ActivityPayload>) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
    data,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

const complete = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/complete`,
    afterReceivingHook: afterReceiving,
  })
}

const softDelete = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
    afterReceivingHook: afterReceiving,
  })
}

export default (axios: AxiosInstance) => ({
  list: list(useAxios(axios)),
  getById: getById(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
  complete: complete(useAxios(axios)),
  softDelete: softDelete(useAxios(axios)),
})
